import { useState } from 'react'
import { UsersHeader } from './UsersHeader'
import { UsersTable } from './UsersTable'
import { type PageInfo } from '../ui'
import {
  type GroupOption,
  type CompanyOption,
  type CreateUserInput,
  type CreateGroupOwnerInput,
  type CreateSuperAdminInput,
  type CreateComplianceOfficerInput,
} from '../modals/UnifiedCreateUserModal'
import { type UserOption } from '../modals/AddExistingGroupOwnerModal'
import type { UserRole } from '../../types/auth'

export interface UserData {
  id: string
  username: string
  email: string
  role: UserRole

  isWhitelisted: boolean
  isActive: boolean
  lastActive: string
  companiesCount: number
  groupsCount: number
}

export interface UsersTabViewProps {
  users: UserData[]
  loading?: boolean
  // searchTerm: string
  selectedRole: string
  // onSearchChange: (search: string) => void
  onRoleChange: (role: string) => void
  title?: string
  showCreateButton?: boolean
  showAddExistingButton?: boolean
  showViewPermissionsButton?: boolean
  showAllRolesInPermissions?: boolean
  onManageUser?: (user: UserData) => void

  // Context data
  groupId?: string
  companyId?: string
  adminOnly?: boolean

  // Data for modals
  groups?: GroupOption[]
  companies?: CompanyOption[]
  availableUsers?: UserOption[]
  usersLoading?: boolean

  // Callbacks
  onCreateUser?: (input: CreateUserInput) => Promise<void>
  onCreateGroupOwner?: (input: CreateGroupOwnerInput) => Promise<void>
  onCreateSuperAdmin?: (input: CreateSuperAdminInput) => Promise<void>
  onCreateComplianceOfficer?: (input: CreateComplianceOfficerInput) => Promise<void>
  onAddUserToGroup?: (userId: string, groupId: string, asAdmin: boolean) => Promise<void>
  onAddUserToCompany?: (userId: string, companyId: string) => Promise<void>
  onUserSearchChange?: (search: string) => void
  onValidationError?: (field: string, message: string) => void
  onUnassignUserFromCompany?: (userId: string, companyId: string) => Promise<void>
  availableRoleFilters?: string[]
  allowedRoles?: UserRole[]
  onUnassignUserFromGroup?: (userId: string, groupId: string) => Promise<void>
  // Pagination props
  pageInfo?: PageInfo
  totalCount?: number
  onNextPage?: (cursor: string) => void
  onPreviousPage?: (cursor: string) => void
  showPagination?: boolean
}

export function UsersTabView({
  users,
  loading = false,
  selectedRole,
  onRoleChange,
  title = 'Users',
  showCreateButton = false,
  showAddExistingButton = false,
  showViewPermissionsButton = false,
  showAllRolesInPermissions = false,
  onManageUser,
  groupId,
  companyId,
  adminOnly = false,
  groups = [],
  companies = [],
  availableUsers = [],
  usersLoading = false,
  onCreateUser,
  onCreateGroupOwner,
  onCreateSuperAdmin,
  onCreateComplianceOfficer,
  onAddUserToGroup,
  onAddUserToCompany,
  onUserSearchChange,
  onValidationError,
  availableRoleFilters = ['TRADER', 'GROUP_OWNER', 'SUPER_ADMIN', 'COMPLIANCE'],
  onUnassignUserFromGroup,
  // Pagination props
  pageInfo,
  totalCount = 0,
  onNextPage,
  onPreviousPage,
  showPagination = false,
  onUnassignUserFromCompany,
  allowedRoles,
}: UsersTabViewProps) {
  const [modalHandlers, setModalHandlers] = useState<{
    handleViewPermissionsClick: () => void
    handleCreateClick: () => void
    handleAddExistingClick: () => void
  } | null>(null)

  return (
    <div className="space-y-4">
      <UsersHeader
        title={title}
        selectedRole={selectedRole}
        onRoleChange={onRoleChange}
        showViewPermissionsButton={showViewPermissionsButton}
        showCreateButton={showCreateButton}
        showAddExistingButton={showAddExistingButton}
        onViewPermissionsClick={modalHandlers?.handleViewPermissionsClick}
        onCreateClick={modalHandlers?.handleCreateClick}
        onAddExistingClick={modalHandlers?.handleAddExistingClick}
        availableRoleFilters={availableRoleFilters}
        allowedRoles={allowedRoles}
      />

      <UsersTable
        users={users}
        loading={loading}
        showViewPermissionsButton={showViewPermissionsButton}
        showCreateButton={showCreateButton}
        showAddExistingButton={showAddExistingButton}
        showAllRolesInPermissions={showAllRolesInPermissions}
        onManageUser={onManageUser}
        groupId={groupId}
        companyId={companyId}
        adminOnly={adminOnly}
        groups={groups}
        companies={companies}
        availableUsers={availableUsers}
        usersLoading={usersLoading}
        onCreateUser={onCreateUser}
        onCreateGroupOwner={onCreateGroupOwner}
        onCreateSuperAdmin={onCreateSuperAdmin}
        onCreateComplianceOfficer={onCreateComplianceOfficer}
        onAddUserToGroup={onAddUserToGroup}
        onAddUserToCompany={onAddUserToCompany}
        onUserSearchChange={onUserSearchChange}
        onValidationError={onValidationError}
        pageInfo={pageInfo}
        totalCount={totalCount}
        onNextPage={onNextPage}
        onPreviousPage={onPreviousPage}
        showPagination={showPagination}
        onModalHandlersReady={setModalHandlers}
        onUnassignUserFromCompany={onUnassignUserFromCompany}
        onUnassignUserFromGroup={onUnassignUserFromGroup}
        allowedRoles={allowedRoles}
      />
    </div>
  )
}
